import { Component, OnInit, OnDestroy, signal, computed, inject, HostListener } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { PassportService, PassportRecord } from '../shared/passport.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { StatusTabsComponent } from './status-tabs.component';
import { UiStateService } from '../shared/ui-state.service';
import { PassportCellComponent } from './passport-cell.component';
import { SyncService } from '../shared/sync.service';
import { environment } from '../../environments/environment';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { PassportHelperService } from '../shared/passport-helper.service';
import { DateTimeHelperService } from '../shared/date-time-helper.service';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';
import { AuthUtilsService } from '../shared/auth-utils.service';
import { ApiService } from '../shared/api.service';
import { NavigationService } from '../shared/navigation.service';
import { NoPassportsComponent } from '../shared/no-passports.component';
import { LoadingStateService } from '../shared/loading-state.service';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { HistoryPanelService } from '../shared/history-panel.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { DesktopStatusRowComponent, FormattedDateComponent, ActionButtonsComponent, DesktopPaginationComponent } from '../shared/desktop-table-templates.component';
import { DesktopEmptyStateComponent } from '../shared/desktop-empty-state.component';
import { OfflineHistoryService } from '../shared/offline-history.service';
import { ScrollPositionService } from '../shared/scroll-position.service';
import { ToastService } from '../shared/toast.service';
import { OutgoingPassportService } from '../shared/outgoing-passport.service';
import { PERSONA_NAVBAR_ITEMS } from '../shared/constants/navbar.constant';
import {
  clearGrowersListRestoreState,
  normalizeGrowersDesktopShowRows,
  parseGrowersListSnapshot,
  peekGrowersExpectRestore,
  saveGrowersListSnapshot,
  type GrowersListSnapshot
} from './growers-list-state.storage';

@Component({
  selector: 'app-growers',
  standalone: true,
  imports: [HeaderComponent, NavbarComponent, StatusTabsComponent, PassportCellComponent, CommonModule, LoadingSpinnerComponent, DateWithOrdinalPipe, FormsModule, NoPassportsComponent, DesktopStatusRowComponent, FormattedDateComponent, ActionButtonsComponent, DesktopPaginationComponent, DesktopEmptyStateComponent],
  templateUrl: './growers.component.html',
  styleUrl: './growers.component.css'
})
export class GrowersComponent implements OnInit, OnDestroy {
  /** Navbar items constant for template */
  readonly navbarItems = PERSONA_NAVBAR_ITEMS;
  
  data: unknown = null;
  /** Filter dropdown definitions; signal so list computeds re-run when options rebuild. */
  readonly growerFilters = signal<Array<{ id: string; label: string; options: string[] }>>([]);
  isLoading = signal(false);
  /** Tracks whether this is the first data load (login / persona switch).
   *  While true, ALL offline-caching operations are awaited so the spinner
   *  stays visible until IndexedDB is fully populated. After the initial
   *  load completes it is set to false so subsequent reloads (syncComplete$,
   *  pull-to-refresh, coming back online) use fire-and-forget caching. */
  isFirstLoad = true;
  private offlineCacheInProgress = false;
  private readonly dateTimeHelper = inject(DateTimeHelperService);
  private readonly commentsPanelService = inject(CommentsPanelService);
  private readonly historyPanelService = inject(HistoryPanelService);
  private readonly offlineHistoryService = inject(OfflineHistoryService);
  private readonly scrollPositionService = inject(ScrollPositionService);
  private readonly toastService = inject(ToastService);
  
  /** Subscription for sync completion events to auto-refresh data */
  private syncSubscription?: Subscription;

  /**
   * Get the persona name for this component
   * Always returns 'grower' to ensure correct roleId regardless of localStorage
   */
  private get personaName(): string {
    return 'grower';
  }

  // Desktop View State
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  currentPage: number = 1;
  itemsPerPage: number = 10;
  rowsDropdownOpen = false;
  Math = Math;
  infoVisible = false;
  infoTooltipStyle: { left: number; top: number } | null = null;
  private infoTimer: any;
  private readonly desktopBreakpoint = 768;
  isDesktopView = false;

  /** When set, applied after passports load (returning from passport details). */
  private pendingGrowersRestore: GrowersListSnapshot | null = null;
  private passportIdToScrollAfterRestore: string | null = null;
  isCancelled = (id: string): boolean => {
    return (this.detailsStore.get(id)?.loadStatusName || '').toLowerCase() === 'cancelled';
  };

  /**
   * Track by function for ngFor
   */
  trackById = PassportService.trackById;

  get sortedPaginatedRecords(): PassportRecord[] {
    let records = [...this.visibleRecords()];

    // Sorting
    if (this.sortColumn) {
      records.sort((a, b) => {
        const detailsA = this.detailsStore.get(a.id);
        const detailsB = this.detailsStore.get(b.id);
        const { valA, valB } = this.helper.getSortValues(this.sortColumn, a, b, detailsA, detailsB);
        return this.helper.compareValues(valA, valB, this.sortDirection);
      });
    }

    // Pagination
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return records.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get totalPages(): number {
    return this.helper.getTotalPages(this.visibleRecords().length, this.itemsPerPage);
  }

  onSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  onPageChange(page: number): void {
    const maxPages = this.helper.getTotalPages(this.visibleRecords().length, this.itemsPerPage);
    if (page >= 1 && page <= maxPages) {
      this.currentPage = page;
    }
  }

  /**
   * Handle status tab selection
   */
  onStatusTabSelect(status: 'pending' | 'outgoing' | 'completed'): void {
    this.ui.setStatusSelected(status);
    this.currentPage = 1;
    this.rebuildFilters();
  }

  /**
   * Toggle rows dropdown visibility
   */
  toggleRowsDropdown(event?: Event): void {
    if (event) event.stopPropagation();
    this.rowsDropdownOpen = !this.rowsDropdownOpen;
  }

  /**
   * Set items per page
   */
  setItemsPerPage(count: number): void {
    this.itemsPerPage = count;
    this.currentPage = 1;
    this.rowsDropdownOpen = false;
  }

  /**
   * Close dropdowns when clicking outside
   */
  @HostListener('document:click')
  closeDropdowns(): void {
    this.rowsDropdownOpen = false;
  }

  /**
   * Rebuild filters from current passport records
   * Uses all records (not filtered) to ensure filter options remain available
   */
  protected rebuildFilters(): void {
    const records = this.passports.records();
    const apnSet = new Set<string>();
    const originSet = new Set<string>();
    const destinationSet = new Set<string>();
    let hasEmptyOrigin = false;
    
    for (const rec of records) {
      const details = this.detailsStore.get(rec.id);
      if (!details) continue;
      
      // Prefer APN Name, fallback to APN code
      const apnVal = details?.apnName || details?.apn;
      
      if (apnVal) apnSet.add(apnVal);
      
      // Check if this record has an empty origin
      if (details?.origin?.trim()) {
        originSet.add(details.origin);
      } else {
        hasEmptyOrigin = true;
      }
      
      // Use weighbridge destination (from weighbridge data) with fallback to legacy destination
      const destinationVal = details?.destinationWeighbridge || details?.destination;
      if (destinationVal) destinationSet.add(destinationVal);
    }

    // Sort origin options, keeping "No Origin Field" first if applicable
    const sortedOrigins = Array.from(originSet).sort((a, b) => a.localeCompare(b));
    const originOptions = hasEmptyOrigin ? [this.helper.NO_ORIGIN_FIELD_TEXT, ...sortedOrigins] : sortedOrigins;

    this.growerFilters.set([
      { id: 'apnName', label: 'APN/Name', options: Array.from(apnSet).sort((a, b) => a.localeCompare(b)) },
      { id: 'origin', label: 'Origin', options: originOptions },
      { id: 'destination', label: 'Destination', options: Array.from(destinationSet).sort((a, b) => a.localeCompare(b)) }
    ]);
  }

  /**
   * Load passport main details into the store
   */
  protected loadPassportMainDetails(passports: PassportMainWithDraftDto[]): void {
    for (const p of passports) {
      const id = p.passportID?.toString() || '';
      if (!id) continue;
      
      this.detailsStore.update(id, {
        id: id,
        loadNumber: p.loadNumber || '',
        variety: p.varietyName || p.variety || '',
        haulier: p.haulierName || '',
        bulkerNumber: p.trailerNumber || '',
        bulkerMain: p.trailerMain || '',
        vendorNumber: p.vendorNumber || '',
        vendorName: p.vendorName || '',
        origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
        destination: p.destinationName || p.destination || '',
        destinationWeighbridge: p.destinationWeighbridge || '',
        apn: p.apn || '',
        apnName: p.apnName || '',
        dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || ''),
        plannedDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.plannedDateTime || ''),
        loadingDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.loadingDateTime || ''),
        imageCount: p.imageCount || 0,
        haccpCheck: p.haccpCheck ? 'Yes' : 'No',
        haccpImageLinks: p.haccpImageLinks || [],
        deletedImageFilenames: [],
        haccpImageFiles: [],
        hasSeenPcrChanges: p.hasSeenPcrChanges || false,
        isPassportCompleted: p.isPassportCompleted || false,
        statusCode: p.statusCode || '',
        loadStatusName: p.loadStatusName || '',
        authorisedBy: p.authorizerName || '',
        originGroupKey: [
          p.regionCode ?? '',
          p.seasonCode ?? '',
          p.vendorNumber ?? '',
          p.varietyGUID ?? '',
          p.certificationGUID ?? ''
        ].join('|')
      });
    }
  }

  openPassportDetails(id: string): void {
    const filtersRaw = this.filterService.filters();
    const filtersClone: Record<string, string[]> = {};
    for (const key of Object.keys(filtersRaw)) {
      const vals = filtersRaw[key];
      filtersClone[key] = vals ? [...vals] : [];
    }

    const tab = this.ui.statusSelected();
    const scrollY =
      globalThis.window === undefined
        ? undefined
        : globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0;

    const showRowsPerPage = normalizeGrowersDesktopShowRows(this.itemsPerPage);

    saveGrowersListSnapshot({
      tab,
      filters: filtersClone,
      searchQuery: this.searchService.query(),
      currentPage: this.currentPage,
      itemsPerPage: showRowsPerPage,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      passportId: id,
      scrollY: scrollY === 0 ? undefined : scrollY
    });

    this.ui.setExpandedRecord(id);

    // Capture original snapshot for offline history comparison (mobile view only)
    if (this.shouldUseOfflineFeatures()) {
      const originalData = this.detailsStore.get(id);
      if (originalData) {
        this.offlineHistoryService.setOriginalSnapshot(id, originalData);
      }
    }

    this.scrollPositionService.saveScrollPosition('/growers');
    void this.router.navigate(['/passport', id], { state: { from: 'growers' } });
  }

  /**
   * Build header object for comments/history panels
   */
  private buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string } | undefined {
    const details = this.detailsStore.get(id);
    return this.helper.buildPanelHeader(details, this.dateTimeHelper, false);
  }

  openComments(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.commentsPanelService.openCommentsPanel(id, header);
    }
  }

  openHistory(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.historyPanelService.openHistoryPanel(id, header);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateDesktopViewState();
  }

  private updateDesktopViewState(force = false): void {
    const nextValue = globalThis.window === undefined ? false : globalThis.window.innerWidth >= this.desktopBreakpoint;
    const shouldUpdate = force || nextValue !== this.isDesktopView;
    if (shouldUpdate) {
      const wasDesktop = this.isDesktopView;
      this.isDesktopView = nextValue;
      this.applyDesktopViewConstraints();
      if (this.isDesktopView && (force || !wasDesktop)) {
        this.applyDesktopDefaultSort();
      }
      this.rebuildFilters();
    }
  }

  private applyDesktopDefaultSort(): void {
    if (!this.sortColumn) {
      this.sortColumn = 'scheduled';
      this.sortDirection = 'asc';
    }
  }

  private applyDesktopViewConstraints(): void {
    if (this.pendingGrowersRestore !== null) {
      return;
    }
    if (this.isDesktopView && this.ui.statusSelected() === 'outgoing') {
      this.ui.setStatusSelected('pending');
      this.currentPage = 1;
    }
  }

  /**
   * Check if record details match the search query
   */
  private matchesSearchQuery(details: any, searchQuery: string): boolean {
    if (!searchQuery) return true;
    
    const originSearchValue = details?.origin?.trim() ? details.origin : this.helper.NO_ORIGIN_FIELD_TEXT;
    const searchFields = [
      details?.loadNumber,
      details?.vendorName,
      details?.vendorNumber,
      details?.variety,
      originSearchValue,
      details?.destinationWeighbridge || details?.destination,
      details?.haulier,
      details?.apnName
    ].map(f => (f || '').toLowerCase());

    return searchFields.some(f => f.includes(searchQuery));
  }

  /**
   * Check if record details match all active filters
   */
  private matchesActiveFilters(details: any, activeFilters: Record<string, string[]>): boolean {
    for (const filter of this.growerFilters()) {
      const filterValues = activeFilters[filter.id];
      if (!filterValues?.length) continue;
      if (!this.matchesSingleFilter(details, filter.id, filterValues)) return false;
    }
    return true;
  }

  /**
   * Check if record matches a single filter
   */
  private matchesSingleFilter(details: any, filterId: string, filterValues: string[]): boolean {
    if (filterId === 'origin') {
      return this.matchesOriginFilter(details, filterValues);
    }
    const recordValue = this.getFilterRecordValue(details, filterId);
    return filterValues.includes(recordValue);
  }

  /**
   * Special handling for origin filter - "No Origin Field" matches empty origins
   */
  private matchesOriginFilter(details: any, filterValues: string[]): boolean {
    const originValue = details?.origin?.trim() || '';
    if (filterValues.includes(this.helper.NO_ORIGIN_FIELD_TEXT) && !originValue) {
      return true;
    }
    return filterValues.includes(originValue);
  }

  /**
   * Get the record value for a specific filter type
   */
  private getFilterRecordValue(details: any, filterId: string): string {
    if (filterId === 'apnName') {
      return details?.apnName || details?.apn || '';
    }
    if (filterId === 'destination') {
      return details?.destinationWeighbridge || details?.destination || '';
    }
    return details?.[filterId] || '';
  }

  private shouldUseOfflineFeatures(): boolean {
    return !this.isDesktopView;
  }

  onPlannedInfoClick(event: MouseEvent): void {
    this.handlePlannedInfoEvent(event);
  }

  onPlannedInfoHover(event: MouseEvent): void {
    this.handlePlannedInfoEvent(event);
  }

  private handlePlannedInfoEvent(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.infoTooltipStyle = {
      left: rect.left + rect.width / 2,
      top: rect.bottom + 12
    };
    this.showPlannedInfo();
  }

  onPlannedInfoLeave(): void {
    this.hidePlannedInfo();
  }

  private showPlannedInfo(): void {
    this.infoVisible = true;
    if (this.infoTimer) clearTimeout(this.infoTimer);
    this.infoTimer = setTimeout(() => {
      this.hidePlannedInfo();
    }, 3000);
  }

  private hidePlannedInfo(): void {
    this.infoVisible = false;
    this.infoTooltipStyle = null;
    if (this.infoTimer) {
      clearTimeout(this.infoTimer);
      this.infoTimer = null;
    }
  }

  async onPlannedDateChange(id: string, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    let rawValue = input.value;
    
    if (rawValue) {
      const dt = new Date(rawValue);
      const mins = dt.getMinutes();
      const rounded = Math.round(mins / 5) * 5;
      if (mins !== rounded) {
        dt.setMinutes(rounded, 0, 0);
        const y = dt.getFullYear();
        const mo = String(dt.getMonth() + 1).padStart(2, '0');
        const d = String(dt.getDate()).padStart(2, '0');
        const h = String(dt.getHours()).padStart(2, '0');
        const mi = String(dt.getMinutes()).padStart(2, '0');
        rawValue = `${y}-${mo}-${d}T${h}:${mi}`;
        input.value = rawValue;
      }
    }
    
    // Use default old date when cleared instead of empty string
    const isoUtc = rawValue ? new Date(rawValue).toISOString() : this.dateTimeHelper.getClearedPlannedDate();
    
    // Optimistic update - store empty for UI purposes when cleared
    this.detailsStore.update(id, { plannedDateTime: rawValue ? isoUtc : '' });
    
    await this.persistPlannedDateTime(id, isoUtc);
  }

  openPlannedDatePicker(input: HTMLInputElement | null | undefined): void {
    if (!input) return;
    // Chromium-based browsers support showPicker() for date/time inputs.
    const anyInput = input as any;
    if (typeof anyInput.showPicker === 'function') {
      anyInput.showPicker();
      return;
    }
    // Fallback: focus + click usually opens the picker.
    input.focus();
    input.click();
  }

  openPlannedDatePickerFromWrapper(event: Event): void {
    // Find the datetime-local input inside the clicked planned cell and open it.
    const host = event.currentTarget as HTMLElement | null;
    if (!host) return;
    const input = host.querySelector('input[type="datetime-local"]');
    this.openPlannedDatePicker(input ? input as HTMLInputElement : null);
  }
  
  private async persistPlannedDateTime(passportId: string, plannedIsoUtc: string): Promise<void> {
      try {
        const token = localStorage.getItem('accessToken');
        const tokenType = localStorage.getItem('tokenType') || 'Bearer';
        // FIX: Use component's personaName instead of localStorage to ensure correct roleId
        const persona = this.personaName; // Always use 'grower' for growers component
        const roleId = this.helper.getRoleIdForPersona(persona);
        if (!token || !passportId) return;
  
        const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/update/${passportId}/role/${roleId}`;
        const headers = {
          'Authorization': `${tokenType} ${token}`
        } as const;
        const body = new FormData();
        body.append('plannedDateTime', plannedIsoUtc);
        
        await firstValueFrom(this.http.put<any>(apiUrl, body, { headers, withCredentials: true }));
      } catch (err: unknown) {
        if (
          err instanceof HttpErrorResponse &&
          err.status === 400 &&
          err.error?.error === 'stale_invoiced_load'
        ) {
          this.toastService.warning(
            err.error?.message ?? 'This load has been invoiced. The list was refreshed with the latest data.',
            7000,
          );
          await this.loadPassportsFromAPI({ silent: true, preserveScrollRoute: '/growers' });
          return;
        }
        console.warn('Failed to persist plannedDateTime:', err);
      }
  }
  
  getPlannedDateDisplay(iso: string): { text: string; date?: string; time?: string } {
      if (!iso) return { text: 'Select' };
      
      // Use DateWithOrdinal logic manualy or just use standard formatting
      // User requested: Wed, 6th Nov 2024, 13:10
      // I can use DateTimeHelperService helper
      
      // Note: The helper formatScheduledText might not include year or 24hr time exactly as requested
      // Let's assume formatScheduledText is close enough or implement custom
      
      try {
          const dt = new Date(iso);
          const datePart = dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
          const timePart = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
          const datePartWithOrdinal = this.dateTimeHelper.addOrdinalToDateString(datePart);
          return { text: '', date: datePartWithOrdinal, time: timePart };
      } catch {
          return { text: iso };
      }
  }

  toInputLocal(iso: string): string {
    return this.dateTimeHelper.convertUtcToLocal(iso);
  }

  /**
   * Get the minimum date/time for the planned date picker (current date/time)
   * Delegates to shared DateTimeHelperService
   */

  get headerFilters(): Array<{ id: string; label: string; options: string[] }> {
    return this.growerFilters();
  }

  constructor(
    private readonly http: HttpClient, 
    public searchService: SearchService, 
    public filterService: FilterService, 
    public passports: PassportService, 
    public ui: UiStateService, 
    public detailsStore: PassportDetailsStore,
    private readonly router: Router,
    private readonly offlineStorage: OfflineStorageService,
    public syncService: SyncService,
    public commentsNotification: CommentsNotificationService,
    private readonly helper: PassportHelperService,
    private readonly authUtils: AuthUtilsService,
    private readonly apiService: ApiService,
    private readonly navigationService: NavigationService,
    private readonly loadingState: LoadingStateService,
    private readonly outgoingPassportService: OutgoingPassportService
  ) {}

  /** Records matching debounced search + filters (status-agnostic); single expensive pass per dependency change. */
  readonly filteredAllStatusRecords = computed(() => {
    try {
      const searchQuery = (this.searchService.filterQuery() || '').toLowerCase().trim();
      const activeFilters = this.filterService.filters();
      return this.passports.records().filter(r => {
        const details = this.detailsStore.get(r.id);
        return (
          this.matchesSearchQuery(details, searchQuery) &&
          this.matchesActiveFilters(details, activeFilters)
        );
      });
    } catch {
      return [];
    }
  });

  readonly pendingCount = computed(() => {
    try {
      return this.filteredAllStatusRecords().filter(r => r.status === 'pending').length;
    } catch {
      return 0;
    }
  });

  readonly outgoingCount = computed(() => {
    try {
      return this.filteredAllStatusRecords().filter(r => r.status === 'outgoing').length;
    } catch {
      return 0;
    }
  });

  readonly completedCount = computed(() => {
    try {
      return this.filteredAllStatusRecords().filter(r => r.status === 'completed').length;
    } catch {
      return 0;
    }
  });

  readonly visibleRecords = computed((): PassportRecord[] => {
    try {
      const selectedStatus = this.ui.statusSelected();
      const filtered = this.filteredAllStatusRecords().filter(r => r.status === selectedStatus);
      return [...filtered].sort((a, b) => {
        const detailsA = this.detailsStore.get(a.id);
        const detailsB = this.detailsStore.get(b.id);
        const dateA = detailsA?.dueDateTime || detailsA?.dateTime || '';
        const dateB = detailsB?.dueDateTime || detailsB?.dateTime || '';
        return dateA.localeCompare(dateB);
      });
    } catch {
      return [];
    }
  });

  ngOnInit(): void {
    const restoring = peekGrowersExpectRestore();
    let snapshot = restoring ? parseGrowersListSnapshot() : null;
    if (restoring && !snapshot) {
      clearGrowersListRestoreState();
      snapshot = null;
    }
    this.pendingGrowersRestore = snapshot;

    if (!snapshot) {
      // Clear filters and search when entering this persona (e.g., switching from another persona)
      this.filterService.clear();
      this.searchService.clear();
      this.ui.setSearchOpen(false);
      this.ui.setFiltersOpen(false);
    }

    this.updateDesktopViewState(true);

    // Subscribe to sync completion events to auto-refresh data after coming back online (mobile only)
    if (this.shouldUseOfflineFeatures()) {
      this.syncSubscription = this.syncService.syncComplete$.subscribe(() => {
        console.log('📥 Sync completed - refreshing passport data');
        void this.loadPassportsFromAPI();
      });
    }

    // Check if we should skip reload (returning from detail in mobile view)
    if (this.shouldSkipDataReload()) {
      // Just restore scroll position, don't reload data
      this.scrollPositionService.restoreScrollPositionAfterDataLoad('/growers');
      // Still initialize comments notification service
      this.commentsNotification.initialize();
      // Refresh data in the background without disrupting scroll position
      void this.loadPassportsFromAPI({ silent: true, preserveScrollRoute: '/growers' });
      return;
    }

    void (async () => {
      await this.loadPassportsFromAPI();

      if (this.pendingGrowersRestore) {
        this.applyGrowersListRestore(this.pendingGrowersRestore);
        this.pendingGrowersRestore = null;
      }

      // Initial sync is complete – all subsequent loads use fire-and-forget caching
      this.isFirstLoad = false;

      // Restore scroll position after data is loaded and rendered
      this.scrollPositionService.restoreScrollPositionAfterDataLoad('/growers');
      this.tryScrollRestoredPassportIntoView();
      
      // Initialize comments notification service
      this.commentsNotification.initialize();
      
      // Debug: Log the current notification count
      setTimeout(() => {
        const currentCount = this.commentsNotification.unreadCount();
        console.log('🔍 Current notification count after init:', currentCount);
        console.log('🔍 Comments in service:', this.commentsNotification.getComments());
      }, 500);
      
      // Check for outgoing records and sync if online (mobile view only)
      if (this.shouldUseOfflineFeatures() && navigator.onLine) {
        const outgoingCount = this.passports.records().filter(r => r.status === 'outgoing' && !r.isSynced).length;
        if (outgoingCount > 0) {
          console.log(`Found ${outgoingCount} outgoing records - triggering sync`);
          await this.syncService.syncOutgoingRecords();
        }
      }
    })();
  }

  /**
   * Check if we should skip data reload (silent return from detail page)
   * Returns true if:
   * 1. We're in mobile view (where scroll preservation matters)
   * 2. Data already exists in the passport service
   * 3. We're returning from a detail page (not a fresh app load)
   */
  private shouldSkipDataReload(): boolean {
    if (peekGrowersExpectRestore()) {
      return false;
    }
    // Only skip in mobile view
    if (this.isDesktopView) return false;
    
    // Only skip if data already exists
    if (this.passports.records().length === 0) return false;
    
    // Only skip if returning from detail page
    return this.scrollPositionService.isReturning();
  }

  private applyGrowersListRestore(snapshot: GrowersListSnapshot): void {
    this.filterService.clear();
    this.searchService.clear();
    this.ui.setStatusSelected(snapshot.tab);
    for (const key of Object.keys(snapshot.filters)) {
      const values = snapshot.filters[key];
      if (values?.length) {
        this.filterService.setFilter(key, values);
      }
    }
    this.searchService.setQuery(snapshot.searchQuery);
    this.searchService.syncFilterQueryWithQuery();
    this.rowsDropdownOpen = false;
    this.itemsPerPage = normalizeGrowersDesktopShowRows(snapshot.itemsPerPage);
    this.sortColumn = snapshot.sortColumn;
    this.sortDirection = snapshot.sortDirection;
    this.currentPage = snapshot.currentPage;
    this.rebuildFilters();
    const totalPages = this.helper.getTotalPages(this.visibleRecords().length, this.itemsPerPage);
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    this.passportIdToScrollAfterRestore = snapshot.passportId;
    clearGrowersListRestoreState();
  }

  private tryScrollRestoredPassportIntoView(): void {
    const id = this.passportIdToScrollAfterRestore;
    if (!id) {
      return;
    }
    this.passportIdToScrollAfterRestore = null;
    if (globalThis.document === undefined) {
      return;
    }
    requestAnimationFrame(() => {
      const safeId = id.replaceAll('"', '');
      const candidates = globalThis.document.querySelectorAll(`[data-passport-id="${safeId}"]`);
      const el = Array.from(candidates).find(e => (e as HTMLElement).offsetParent !== null);
      el?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }

  onNavChange(id: string): void {
    this.navigationService.onNavChange(id);
  }

  ngOnDestroy(): void {
    // Clean up sync subscription to prevent memory leaks
    this.syncSubscription?.unsubscribe();
    this.searchService.cancelDebouncedFilterUpdate();
  }

  private async loadPassportsFromAPI(options?: { silent?: boolean; preserveScrollRoute?: string }): Promise<void> {
    if (this.isLoading()) return;

    const isSilent = options?.silent === true;
    const preserveScrollRoute = options?.preserveScrollRoute;

    if (!isSilent) {
      this.isLoading.set(true);
    }

    // Only save scroll position when NOT silent - silent refresh should preserve existing position
    if (preserveScrollRoute && !isSilent) {
      this.scrollPositionService.saveScrollPosition(preserveScrollRoute);
    }

    const isOnline = navigator.onLine;

    try {
      const authData = this.authUtils.getAuthData();
      
      // Handle mock token
      if (authData.token === 'mock-token-for-development') {
        await this.handleMockToken();
        return;
      }
      
      // Handle real token
      if (authData.token) {
        await this.handleRealToken(authData, isOnline);
      }
    } catch (error) {
      console.warn('Error loading passports:', error);
      this.handleLoadError(error);
    } finally {
      if (!isSilent) {
        this.isLoading.set(false);
      }
      // Only restore scroll position when NOT silent - silent refresh should preserve existing position
      if (preserveScrollRoute && !isSilent) {
        this.scrollPositionService.restoreScrollPositionAfterDataLoad(preserveScrollRoute);
      }
    }
  }

  private async handleMockToken(): Promise<void> {
    console.log('Using mock token - skipping API call');
    await new Promise(resolve => setTimeout(resolve, 500));
    this.data = { message: 'Using mock data for development' };
    this.convertAndStorePassportMainData([]);
    this.loadPassportMainDetails([]);
    this.rebuildFilters();
  }

  private async handleRealToken(authData: any, isOnline: boolean): Promise<void> {
    const roleId = this.helper.getRoleIdForPersona(authData.persona);
    console.log(`Loading passports for persona '${authData.persona}' with roleId ${roleId}`);

    if (!isOnline) {
      await this.handleOfflineMode();
      return;
    }

    // Make API call
    const passports = await this.fetchPassportsFromAPI(authData.userID, roleId);
    await this.processPassportData(passports, authData);
  }

  private async handleOfflineMode(): Promise<void> {
    if (this.shouldUseOfflineFeatures()) {
      console.log('Device is offline - loading from IndexedDB');
      await this.loadFromOfflineStorage();
      this.data = { message: 'Offline mode - using cached data' };
    } else {
      console.log('Desktop view detected - skipping offline cache load');
      this.data = { message: 'Offline mode is available on mobile devices only' };
    }
  }

  private async fetchPassportsFromAPI(userID: string, roleId: number): Promise<PassportMainWithDraftDto[]> {
    const endpoint = `/api/v1/passports?userId=${userID}&roleId=${roleId}`;
    console.log(`Fetching passports from: ${environment.apiBaseUrl}${endpoint}`);
    
    try {
      const passports = await this.apiService.get<PassportMainWithDraftDto[]>(endpoint);
      console.log(`API Response: ${passports.length} passports received`);
      return passports;
    } catch(e) {
      console.warn('API call failed, continuing with empty data', e);
      return [];
    }
  }

  private async processPassportData(passports: PassportMainWithDraftDto[], authData: any): Promise<void> {
    this.convertAndStorePassportMainData(passports);
    this.loadPassportMainDetails(passports);
    
    if (this.shouldUseOfflineFeatures()) {
      await this.loadAndMergeOutgoingPassports();
    }
    
    this.rebuildFilters();
    
    this.data = {
      message: `Loaded ${passports.length} passports`,
      totalCount: passports.length
    };
    
    console.log(`Successfully loaded ${passports.length} passports for user ${authData.userID}`);

    // Cache data for offline use (excluding HACCP images) - await to keep spinner visible
    if (this.shouldUseOfflineFeatures() && navigator.onLine && authData.persona.toLowerCase().includes('grower')) {
      const roleId = this.helper.getRoleIdForPersona(authData.persona);
      const headers = this.authUtils.createAuthHeaders();
      await this.cacheAllDataForOffline(passports, roleId, headers);

      if (this.isFirstLoad) {
        // First load: await ALL background operations so spinner stays visible
        // until IndexedDB is fully populated with origins and authorised users
        await Promise.all([
          this.cacheOriginsGrouped(passports, roleId, headers).catch(err =>
            console.warn('Background origins caching failed:', err)
          ),
          this.cacheAuthorisedUsers(headers).catch(err =>
            console.warn('Background authorised users caching failed:', err)
          )
        ]);
      } else {
        // Subsequent loads: fire-and-forget (current behavior)
        void this.cacheOriginsGrouped(passports, roleId, headers).catch(err =>
          console.warn('Background origins caching failed:', err)
        );
        void this.cacheAuthorisedUsers(headers).catch(err =>
          console.warn('Background authorised users caching failed:', err)
        );
      }
    }
  }

  private async handleLoadError(error: any): Promise<void> {
    console.warn('Failed to load passports from API:', error);
    console.warn('Error details:', {
      status: error?.status,
      message: error?.message,
      error: error?.error
    });
    
    const isOnline = navigator.onLine;
    
    if (!isOnline) {
      if (this.shouldUseOfflineFeatures()) {
        await this.loadFromOfflineStorage();
        this.data = { message: 'Offline mode - using cached data' };
      } else {
        this.data = { message: 'Offline mode is available on mobile devices only' };
      }
      return;
    }

    // Online: Show error message, don't use cache
    this.data = { message: this.getErrorMessage(error.status) };
  }

  private getErrorMessage(status: number): string {
    const errorMessages: Record<number, string> = {
      401: 'Authentication failed - please login again',
      403: 'Permission denied',
      404: 'API endpoint not found',
      500: 'Server error - please try again later'
    };
    
    const message = errorMessages[status];
    if (message) {
      console.warn(`${message} (status: ${status})`);
    }
    return message || 'Failed to load passports';
  }

  private convertAndStorePassportMainData(passports: PassportMainWithDraftDto[]): void {
    const frontendRecords: PassportRecord[] = passports.map(passport => ({
      id: passport.passportID.toString(),
      status: this.mapStatusCodeToFrontend(passport.statusCode, passport.hasGrowerCompleted),
      isSynced: true // Data from API is considered synced
    }));

    const currentRecords = this.passports.records();
    
    // Preserve: MOCK records AND unsynced outgoing records
    // This ensures outgoing records don't get overwritten during refresh
    const preservedRecords = currentRecords.filter(r => 
      r.id.startsWith('MOCK-') || (r.status === 'outgoing' && !r.isSynced)
    );
    
    // Remove duplicates: API data takes precedence unless record is outgoing
    const preservedIds = new Set(preservedRecords.map(r => r.id));
    const nonConflictingApiRecords = frontendRecords.filter(r => !preservedIds.has(r.id));
    
    // Merge preserved records with non-conflicting API records
    this.passports.records.set([...preservedRecords, ...nonConflictingApiRecords]);
  }

  private mapStatusCodeToFrontend(statusCode?: string, hasGrowerCompleted?: boolean): 'pending' | 'outgoing' | 'completed' {
    // If statusCode is "AWAITING_GROWER_ACTION", it's always pending
    // Any other status (or null/undefined) is considered completed
    if (!statusCode || statusCode.toUpperCase() === 'AWAITING_GROWER_ACTION') {
      return 'pending';
    }
    
    // All other statuses go to completed tab (ignoring hasGrowerCompleted)
    return 'completed';
  }

  async onRefresh(): Promise<void> {
    try {
      this.ui.setRefreshing(true);
      // Yield to the UI so the spinner/overlay is painted before heavy work
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      
      // DEBUG: Show current IndexedDB state at start of refresh
      if (this.shouldUseOfflineFeatures()) {
        try {
          const outgoing = await this.offlineStorage.getAllOutgoingPassports();
          const drafts = await this.offlineStorage.getAllDrafts();
          const cached = await this.offlineStorage.getAllPassports();
          this.toastService.info(
            `📊 State: ${outgoing.length} outgoing, ${drafts.length} drafts, ${cached.length} cached`,
            4000
          );
        } catch (e) {
          console.warn('Failed to get debug storage info:', e);
        }
      }
      
      // Sync outgoing records first if online (mobile view only)
      if (this.shouldUseOfflineFeatures() && navigator.onLine) {
        const outgoingCount = this.passports.records().filter(r => r.status === 'outgoing' && !r.isSynced).length;
        if (outgoingCount > 0) {
          console.log(`Syncing ${outgoingCount} outgoing records before refresh`);
          await this.syncService.syncOutgoingRecords();
        }
      }
      
      await this.loadPassportsFromAPI();
      // Refresh comment notification count
      await this.commentsNotification.refresh();
    } finally {
      this.ui.setRefreshing(false);
    }
  }


  // ==================== OFFLINE STORAGE METHODS ====================

  private async loadFromOfflineStorage(): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) {
      console.log('Offline storage load skipped on desktop view');
      return;
    }
    try {
      console.log('Loading passports from IndexedDB...');
      const cachedPassports = await this.offlineStorage.getAllPassports();
      
      if (cachedPassports.length > 0) {
        console.log(`Loaded ${cachedPassports.length} passports from IndexedDB`);
        
        // Convert cached passports to frontend format
        const frontendRecords: PassportRecord[] = cachedPassports.map(p => ({
          id: p.passportID.toString(),
          status: this.mapStatusCodeToFrontend(p.statusCode, p.hasGrowerCompleted),
          isSynced: true
        }));
        
        this.passports.records.set(frontendRecords);
        
        // Load details for each cached passport, including comments and history
        for (const passport of cachedPassports) {
          const passportId = passport.passportID;
          
          // Load comments and history from IndexedDB for this passport
          // Helper functions handle IndexedDB connection errors gracefully
          const cachedComments = await this.safeLoadComments(passportId);
          const cachedHistory = await this.safeLoadHistory(passportId);
          
          this.detailsStore.upsert({
            id: passport.passportID.toString(),
            loadNumber: passport.loadNumber || '',
            dateTime: passport.dueDateTime || passport.scheduledDateTime || '',
            dueDateTime: passport.dueDateTime || passport.scheduledDateTime || '',
            vendorName: passport.vendorName || '',
            vendorNumber: passport.vendorNumber || '',
            apnName: passport.apnName || passport.apn || '',
            variety: passport.variety || '',
            haulier: passport.haulierName || '',
            destination: passport.destination || '',
            destinationWeighbridge: passport.destinationWeighbridge || '',
            origin: this.helper.formatOriginWithType(passport.originName, passport.rawSourceType),
            loadingDateTime: passport.loadingDateTime || '',
            bulkerNumber: passport.trailerNumber || '',
            bulkerMain: passport.trailerMain || '',
            haccpCheck: (passport.haccpCheck === true ? 'Yes' : 'No'),
            haccpImages: [],
            haccpImageLinks: passport.haccpImageLinks || [],  // Store proxy URLs from cache
            imageCount: passport.imageCount || 0,  // Store image count from backend
            deletedImageFilenames: [],  // Clear old editing state
            haccpImageFiles: [],  // Clear old editing state
            authorisedBy: passport.authorizerName || '',
            plantID: passport.plantID || passport.PlantID,
            plannedDateTime: passport.plannedDateTime || '',
            isCancelled: (passport.loadStatusName || '').toLowerCase() === 'cancelled',  // Case-insensitive check
            hasSeenPcrChanges: passport.hasSeenPcrChanges || false,
            statusCode: passport.statusCode || '',
            comments: cachedComments || [],
            history: cachedHistory || [],
            originGroupKey: [
              passport.regionCode ?? '',
              passport.seasonCode ?? '',
              passport.vendorNumber ?? '',
              passport.varietyGUID ?? '',
              passport.certificationGUID ?? ''
            ].join('|')
          });
          
          // Image caching removed: do not load images from offline storage
        }
        
        console.log(`✅ Loaded comments and history for ${cachedPassports.length} passports from IndexedDB`);
        
        // Load outgoing passports from IndexedDB and merge with regular passports
        await this.loadAndMergeOutgoingPassports();
        
        this.rebuildFilters();
      } else {
        console.log('No cached passports found in IndexedDB');
      }
    } catch (error) {
      console.warn('Failed to load from offline storage:', error);
    }
  }

  /**
   * Load outgoing passports from IndexedDB and merge with regular passports.
   * Restores submission fields into detailsStore so the Outgoing list shows pending submit data.
   */
  private async loadAndMergeOutgoingPassports(): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) {
      return;
    }
    await this.outgoingPassportService.mergeOutgoingPassportsWithRecords(
      this.passports,
      this.ui,
      this.detailsStore,
    );
  }

  private async cacheAllDataForOffline(passports: PassportMainWithDraftDto[], roleId: number, headers: any): Promise<void> {
    if (!this.shouldUseOfflineFeatures()) {
      console.log('Skipping offline cache population in desktop view');
      return;
    }
    if (this.offlineCacheInProgress) {
      console.log('Offline cache already in progress, skipping');
      return;
    }

    this.offlineCacheInProgress = true;
    console.log('Starting offline cache for', passports.length, 'passports');

    try {
      // Clear old persona data before caching new data
      await this.offlineStorage.clearPersonaData();
      console.log('Cleared old persona data from IndexedDB');

      // Save all passports to IndexedDB
      await this.offlineStorage.saveAllPassports(passports);
      console.log('Saved passports to IndexedDB');

      // Process passports in true batches - only start 5 at a time
      // Origins are cached separately (grouped by origin-determining attributes)
      const batchSize = 5;
      for (let i = 0; i < passports.length; i += batchSize) {
        const batch = passports.slice(i, i + batchSize);
        
        // Create and execute promises for THIS batch only
        await Promise.all(batch.map(async (passport) => {
          const passportId = passport.passportID;
          try {
            // Fetch and cache comments
            await this.cacheCommentsForPassport(passportId, headers);
            
            // Fetch and cache history
            await this.cacheHistoryForPassport(passportId, headers);
          } catch (err) {
            console.warn(`Failed to cache data for passport ${passportId}:`, err);
          }
        }));
        
        console.log(`Cached batch ${Math.floor(i / batchSize) + 1} of ${Math.ceil(passports.length / batchSize)}`);
      }

      // Store metadata about last sync
      await this.offlineStorage.setMetadata('lastSync', {
        timestamp: Date.now(),
        passportCount: passports.length,
        roleId: roleId
      });

      // Clean up stale passports that are no longer in the API response
      const validIds = passports.map(p => p.passportID.toString());
      const removedCount = await this.offlineStorage.cleanupStalePassports(validIds);
      if (removedCount > 0) {
        this.toastService.info(`Removed ${removedCount} outdated cached passports`, 3000);
      }

      const storageInfo = await this.offlineStorage.getStorageInfo();
      console.log('Offline cache complete:', storageInfo);
    } catch (error) {
      console.warn('Failed to cache data for offline use:', error);
    } finally {
      this.offlineCacheInProgress = false;
    }
  }

  private async cacheCommentsForPassport(passportId: number, headers: any): Promise<void> {
    const authData = this.authUtils.getAuthData();
    if (!authData.userID) {
      console.warn('No userID found, skipping comments cache');
      return;
    }
    const body = {
      PassportId: passportId,
      UserId: Number.parseInt(authData.userID, 10)
    };
    await this.cachePassportData(
      passportId,
      headers,
      'comments',
      `/api/v1/passports/comments`,
      body,
      (id, data) => this.offlineStorage.saveComments(id, data),
      'comments'
    );
  }

  private async cacheHistoryForPassport(passportId: number, headers: any): Promise<void> {
    const authData = this.authUtils.getAuthData();
    await this.cachePassportData(
      passportId,
      headers,
      'history',
      `/api/v1/passports/history?userId=${authData.userID}&passportId=${passportId}`,
      null, // History still uses GET, so body is null
      (id, data) => this.offlineStorage.saveHistory(id, data),
      'history'
    );
  }

  /**
   * Generic helper method to cache passport data (comments/history)
   */
  private async cachePassportData(
    passportId: number,
    headers: any,
    dataType: 'comments' | 'history',
    endpoint: string,
    body: any,
    saveFn: (id: number, data: any[]) => Promise<void>,
    errorLabel: string
  ): Promise<void> {
    try {
      const data = dataType === 'comments' 
        ? await this.apiService.post<any[]>(endpoint, body)
        : await this.apiService.get<any[]>(endpoint);
      
      // API returns array directly, not wrapped in { comments: [...] }
      if (data && Array.isArray(data)) {
        await saveFn(passportId, data);
        console.log(`Cached ${data.length} ${errorLabel} for passport ${passportId}`);
      } else {
        // Handle case where API might return empty array or null
        await saveFn(passportId, []);
        console.log(`No ${errorLabel} to cache for passport ${passportId}`);
      }
    } catch (error: any) {
      if (error?.status === 404) {
        // No data found, save empty array
        await saveFn(passportId, []);
        console.log(`No ${errorLabel} found (404) for passport ${passportId}, cached empty array`);
      } else {
        console.warn(`Failed to fetch ${errorLabel} for passport ${passportId}:`, error);
        // Don't cache on error - let it try again next time
      }
    }
  }

  /**
   * Safely load comments from IndexedDB with error handling
   * Returns empty array on error instead of throwing
   */
  private async safeLoadComments(passportId: number): Promise<any[]> {
    return this.helper.safeLoadPassportData(passportId, 'comments', (id) => this.offlineStorage.getComments(id));
  }

  /**
   * Safely load history from IndexedDB with error handling
   * Returns empty array on error instead of throwing
   */
  private async safeLoadHistory(passportId: number): Promise<any[]> {
    return this.helper.safeLoadPassportData(passportId, 'history', (id) => this.offlineStorage.getHistory(id));
  }

  // Image caching removed
  // Shunting yards caching removed - only needed for Gatehouse and Scheduler personas

  /**
   * Cache authorised users for offline use
   */
  private async cacheAuthorisedUsers(headers: any): Promise<void> {
    try {
      const authData = this.authUtils.getAuthData();
      if (!authData.userID) {
        console.warn('No userID found, skipping authorised users cache');
        return;
      }
      
      const endpoint = `/api/v1/authorizers?userId=${authData.userID}`;
      console.log('Fetching authorised users from:', `${environment.apiBaseUrl}${endpoint}`);
      
      const response = await this.apiService.get<any>(endpoint);
      console.log('Authorised users API response:', response);
      
      if (response?.authorisedUsers) {
        // Deduplicate authorised users by authorizerId before storing
        const seen = new Map<number, any>();
        for (const user of response.authorisedUsers) {
          const key = user.authorizerId ?? user.id;
          if (key != null && !seen.has(key)) {
            seen.set(key, user);
          }
        }
        const uniqueUsers = Array.from(seen.values());
        await this.offlineStorage.saveAuthorisedUsers(this.authUtils.getCurrentUserIdAsNumber(), uniqueUsers);
        console.log(`Cached ${uniqueUsers.length} unique authorised users (${response.authorisedUsers.length} total from API)`);
      }
    } catch (error: any) {
      if (error?.status !== 404) {
        console.warn('Failed to cache authorised users:', error);
      }
    }
  }

  /**
   * Cache origins using grouped approach to minimize API calls.
   * Groups passports by their origin-determining attributes (RegionCode, SeasonCode,
   * VendorNumber, VarietyGUID, CertificationGUID) and fetches origins once per unique group.
   * Falls back to per-passport fetching if grouping attributes are not available.
   */
  private async cacheOriginsGrouped(passports: PassportMainWithDraftDto[], roleId: number, headers: any): Promise<void> {
    // Only cache origins for pending passports (completed passports are view-only)
    const pendingPassports = passports.filter(p => {
      const sc = (p.statusCode || '').toUpperCase();
      return !sc || sc === 'AWAITING_GROWER_ACTION';
    });

    if (pendingPassports.length === 0) {
      console.log('No pending passports, skipping origins cache');
      return;
    }

    console.log(`Caching origins for ${pendingPassports.length} pending passports (skipping ${passports.length - pendingPassports.length} completed)`);

    // Check if grouping attributes are available (requires updated SP)
    const hasGroupingAttributes = pendingPassports.length > 0 && pendingPassports[0].regionCode !== undefined;

    if (!hasGroupingAttributes) {
      // Fallback: fetch origins per passport (old behavior)
      console.log('Origin grouping attributes not available, falling back to per-passport caching');
      for (const passport of pendingPassports) {
        await this.cacheOriginsForPassport(passport.passportID, roleId, headers);
      }
      return;
    }

    const originGroups = this.buildOriginGroups(pendingPassports);
    console.log(`Grouped ${pendingPassports.length} pending passports into ${originGroups.size} distinct origin groups`);

    // Fetch origins once per group, store once per group key
    for (const [groupKey, group] of originGroups) {
      await this.fetchAndCacheOriginGroup(groupKey, group.representativePassportId, group.passportIds.length, roleId);
    }
  }

  /**
   * Group passports by their origin-determining attributes into a Map.
   */
  private buildOriginGroups(passports: PassportMainWithDraftDto[]): Map<string, { representativePassportId: number; passportIds: number[] }> {
    const groups = new Map<string, { representativePassportId: number; passportIds: number[] }>();
    for (const p of passports) {
      const groupKey = [
        p.regionCode ?? '',
        p.seasonCode ?? '',
        p.vendorNumber ?? '',
        p.varietyGUID ?? '',
        p.certificationGUID ?? ''
      ].join('|');

      if (!groups.has(groupKey)) {
        groups.set(groupKey, { representativePassportId: p.passportID, passportIds: [] });
      }
      groups.get(groupKey)!.passportIds.push(p.passportID);
    }
    return groups;
  }

  /**
   * Fetch and cache origins for a single origin group.
   */
  private async fetchAndCacheOriginGroup(groupKey: string, representativePassportId: number, passportCount: number, roleId: number): Promise<void> {
    try {
      const endpoint = `/api/v1/origins?roleId=${roleId}&passportId=${representativePassportId}`;
      const response = await this.apiService.get<any>(endpoint);

      if (!response?.origins || !Array.isArray(response.origins)) {
        console.log(`No origins returned for group (representative passport ${representativePassportId})`);
        return;
      }
      await this.offlineStorage.saveOrigins(roleId, groupKey, response.origins);
      console.log(`Cached ${response.origins.length} origins for group key "${groupKey}" (${passportCount} passports)`);
    } catch (error: any) {
      const msg = `representative passport ${representativePassportId}`;
      if (error?.status === 404) {
        console.log(`No origins found (404) for group (${msg})`);
      } else {
        console.warn(`Failed to cache origins for group (${msg}):`, error);
      }
    }
  }

  /**
   * Cache origins for a specific passport for offline use (fallback method).
   * Uses the origin group key from the details store when available.
   */
  private async cacheOriginsForPassport(passportId: number, roleId: number, headers: any): Promise<void> {
    try {
      const endpoint = `/api/v1/origins?roleId=${roleId}&passportId=${passportId}`;
      
      const response = await this.apiService.get<any>(endpoint);
      
      if (response?.origins && Array.isArray(response.origins)) {
        // Use group key if available, otherwise fall back to passport ID
        const currentData = this.detailsStore.get(passportId.toString());
        const storageKey = currentData?.originGroupKey || passportId.toString();
        await this.offlineStorage.saveOrigins(roleId, storageKey, response.origins);
        console.log(`Cached ${response.origins.length} origins for passport ${passportId} (key: "${storageKey}")`);
      } else {
        console.log(`No origins returned for passport ${passportId}`);
      }
    } catch (error: any) {
      if (error?.status === 404) {
        console.log(`No origins found (404) for passport ${passportId}`);
      } else {
        console.warn(`Failed to cache origins for passport ${passportId}:`, error);
      }
    }
  }

}


